import { Field } from './Field.jsx';
import { inputStyle } from '../lib/ui.js';

export function ShiftEditor({ editor, users, onChange, onSave, onClose, onDelete }) {
  const f = editor.form;
  const update = (k, v) => onChange({ ...f, [k]: v });
  const employee = users.find((u) => u.id === f.employee_id);
  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 10, padding: 20, maxWidth: 480, width: '100%',
                  maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>
          {editor.mode === 'create' ? 'New shift' : 'Edit shift'}
          {editor.source === 'square' && (
            <span style={{ marginLeft: 6, fontSize: 10, padding: '2px 6px', background: '#eceff1', color: '#37474f', borderRadius: 4 }}>
              Pulled from Square
            </span>
          )}
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          <Field label="Employee">
            <select value={f.employee_id || ''} onChange={(e) => update('employee_id', e.target.value ? Number(e.target.value) : null)}
              style={inputStyle}>
              <option value="">— Open / unassigned —</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </Field>
          <Field label="Date">
            <input type="date" value={f.shift_date}
              onChange={(e) => update('shift_date', e.target.value)} style={inputStyle} />
          </Field>
          {/* Multi-day broadcast — only on CREATE. Pick additional days
              within the same week to drop the same shift on. Lets a
              manager write 'Mon/Wed/Fri 8a-2p' once. Editing an
              existing shift hides this since you're operating on one
              row. Open shifts (no employee assigned) work the same. */}
          {editor.mode === 'create' && (
            <Field label="Also create on (optional)">
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {(() => {
                  // Build the 7 days of the same ISO week as f.shift_date,
                  // Mon → Sun. Skip the cell's own date (it's always
                  // included). Remembering selections by ISO date.
                  const base = new Date(f.shift_date + 'T12:00:00Z');
                  const dow = base.getUTCDay();           // 0=Sun
                  const monOffset = dow === 0 ? -6 : 1 - dow;
                  const monday = new Date(base);
                  monday.setUTCDate(base.getUTCDate() + monOffset);
                  const labels = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
                  const days = labels.map((l, i) => {
                    const d = new Date(monday);
                    d.setUTCDate(monday.getUTCDate() + i);
                    const ymd = d.toISOString().slice(0, 10);
                    return { label: l, ymd, isPrimary: ymd === f.shift_date };
                  });
                  const extras = new Set(f.extra_dates || []);
                  function toggle(ymd) {
                    const next = new Set(extras);
                    if (next.has(ymd)) next.delete(ymd); else next.add(ymd);
                    update('extra_dates', Array.from(next));
                  }
                  return days.map(({ label, ymd, isPrimary }) => {
                    const on = isPrimary || extras.has(ymd);
                    return (
                      <button key={ymd} type="button"
                        onClick={() => !isPrimary && toggle(ymd)}
                        title={ymd + (isPrimary ? ' (primary date)' : '')}
                        disabled={isPrimary}
                        style={{ padding: '6px 10px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                                  borderRadius: 6,
                                  border: `1px solid ${on ? 'var(--forest)' : '#d0d4d9'}`,
                                  background: on ? 'var(--forest)' : '#fff',
                                  color: on ? '#fff' : '#37474f',
                                  cursor: isPrimary ? 'default' : 'pointer',
                                  opacity: isPrimary ? 0.85 : 1 }}>
                        {label}
                      </button>
                    );
                  });
                })()}
              </div>
              <div style={{ fontSize: 11, color: '#9e9e9e', marginTop: 4 }}>
                {(f.extra_dates || []).length === 0
                  ? 'Saving creates one shift on the chosen date.'
                  : `Saving creates ${1 + (f.extra_dates || []).length} shifts. Conflicts will be skipped.`}
              </div>
            </Field>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Field label="Start">
              <input type="time" step="1800" value={f.start_time}
                onChange={(e) => update('start_time', e.target.value)} style={inputStyle} />
            </Field>
            <Field label="End">
              <input type="time" step="1800" value={f.end_time}
                onChange={(e) => update('end_time', e.target.value)} style={inputStyle} />
            </Field>
          </div>
          <Field label="Role">
            <input value={f.role || ''} onChange={(e) => update('role', e.target.value)}
              placeholder={employee?.role || 'Barista'} style={inputStyle} />
          </Field>
          <Field label="Break">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[
                { id: 'none',       label: 'None',         min: 0,  paid: true  },
                { id: 'paid10',     label: 'Paid 10 min',  min: 10, paid: true  },
                { id: 'unpaid30',   label: 'Unpaid 30 min',min: 30, paid: false },
              ].map(opt => {
                const min = Number(f.break_minutes ?? 0);
                const isOn =
                  (opt.id === 'none' && min === 0) ||
                  (opt.id === 'paid10' && min === 10 && f.break_paid !== false) ||
                  (opt.id === 'unpaid30' && min === 30 && f.break_paid === false);
                return (
                  <button key={opt.id} type="button"
                    onClick={() => onChange({ ...f, break_minutes: opt.min, break_paid: opt.paid })}
                    style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                              borderRadius: 6,
                              border: `1px solid ${isOn ? 'var(--forest)' : '#d0d4d9'}`,
                              background: isOn ? 'var(--forest)' : '#fff',
                              color: isOn ? '#fff' : '#37474f',
                              cursor: 'pointer' }}>
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: 11, color: '#9e9e9e', marginTop: 4 }}>
              Unpaid breaks are deducted from the scheduled-hours total used for OT tracking.
            </div>
          </Field>
          <Field label="Notes (optional)">
            <input value={f.notes || ''} onChange={(e) => update('notes', e.target.value)}
              placeholder="anything specific" style={inputStyle} />
          </Field>
        </div>
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
          <div>
            {onDelete && (
              <button onClick={onDelete}
                style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600,
                          background: '#fff', color: '#c62828', border: '1px solid #ffcdd2',
                          borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}>
                Delete
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} className="btn-secondary" style={{ fontSize: 12 }}>Cancel</button>
            <button onClick={onSave}
              style={{ background: 'var(--forest)', color: '#fff', border: 'none', borderRadius: 6,
                        padding: '6px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {editor.mode === 'create' ? 'Add shift' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
